import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { CheckCircle, XCircle, Clock, Package, Send } from 'lucide-react';

const API_URL = 'https://i-kept-all-the-code-in-github-and-now-i.onrender.com';

const S = {
  heading: { fontSize: 26, fontWeight: 300, letterSpacing: '0.06em', color: '#f5f0e8', marginBottom: 4 },
  sub: { fontSize: 13, color: 'rgba(245,240,232,0.35)', marginBottom: 28, letterSpacing: '0.03em' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 },
  card: (valid) => ({
    background: '#13110d',
    border: `1px solid ${valid === null ? 'rgba(186,130,69,0.12)' : valid ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`,
    borderRadius: 10, padding: 20,
  }),
};

const AdminReturns = () => {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [trackingInputs, setTrackingInputs] = useState({});
  const [sendingTracking, setSendingTracking] = useState({});

  useEffect(() => {
    const fetchReturns = async () => {
      const { data } = await supabase
        .from('returns')
        .select('*, orders(id, email, customer_name, total_amount, created_at, phone)')
        .order('created_at', { ascending: false });
      setReturns(data || []);
      setLoading(false);
    };
    fetchReturns();
  }, []);

  const updateReturn = async (id, updates) => {
    await supabase.from('returns').update(updates).eq('id', id);
    setReturns(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
  };

  const assignReturnTracking = async (returnId) => {
    const trackingId = trackingInputs[returnId]?.trim();
    if (!trackingId) return;

    setSendingTracking(prev => ({ ...prev, [returnId]: true }));
    try {
      const res = await fetch(`${API_URL}/api/assign-return-tracking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ returnId, returnTrackingId: trackingId }),
      });
      const result = await res.json();
      if (result.success) {
        setReturns(prev => prev.map(r => r.id === returnId ? { ...r, return_tracking_id: trackingId, status: 'in_progress' } : r));
      }
    } catch (err) {
      console.error('Assign return tracking error:', err);
      // Fallback direct update
      await supabase.from('returns').update({ return_tracking_id: trackingId }).eq('id', returnId);
      setReturns(prev => prev.map(r => r.id === returnId ? { ...r, return_tracking_id: trackingId } : r));
    }
    setSendingTracking(prev => ({ ...prev, [returnId]: false }));
  };

  const filtered = returns.filter(r => {
    if (filter === 'valid') return r.is_valid === true;
    if (filter === 'invalid') return r.is_valid === false;
    if (filter === 'pending') return r.is_valid === null;
    return true;
  });

  const counts = {
    all: returns.length,
    valid: returns.filter(r => r.is_valid === true).length,
    invalid: returns.filter(r => r.is_valid === false).length,
    pending: returns.filter(r => r.is_valid === null).length,
  };

  return (
    <div style={{ color: '#f5f0e8' }}>
      <div style={S.heading}>Returns</div>
      <div style={S.sub}>Review and approve return requests · {returns.length} total</div>

      {/* Filter Tabs */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { key: 'all', label: 'All', color: '186,130,69' },
          { key: 'pending', label: 'Pending', color: '234,179,8' },
          { key: 'valid', label: 'Approved', color: '34,197,94' },
          { key: 'invalid', label: 'Rejected', color: '239,68,68' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: filter === tab.key ? `rgba(${tab.color},0.12)` : '#13110d',
              border: `1px solid ${filter === tab.key ? `rgba(${tab.color},0.4)` : 'rgba(186,130,69,0.1)'}`,
              borderRadius: 6, padding: '8px 16px',
              color: filter === tab.key ? `rgb(${tab.color})` : 'rgba(245,240,232,0.4)',
              fontSize: 13, cursor: 'pointer',
              fontFamily: "'Cormorant Garamond', Georgia, serif",
            }}
          >
            {tab.label}
            <span style={{
              background: `rgba(${tab.color},0.15)`, color: `rgb(${tab.color})`,
              borderRadius: 10, padding: '1px 7px', fontSize: 11,
            }}>
              {counts[tab.key]}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ color: 'rgba(245,240,232,0.2)', textAlign: 'center', padding: 48 }}>Loading returns...</div>
      ) : (
        <div style={S.grid}>
          {filtered.map(ret => (
            <div key={ret.id} style={S.card(ret.is_valid)}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 11, letterSpacing: '0.2em', color: 'rgba(245,240,232,0.3)', textTransform: 'uppercase', marginBottom: 4 }}>Return #{ret.id}</div>
                  <div style={{ color: '#ba8245', fontSize: 15 }}>Order #{ret.order_id}</div>
                </div>
                {ret.is_valid === null ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.3)', borderRadius: 20, padding: '4px 10px', color: '#fde047', fontSize: 11 }}>
                    <Clock size={11} /> Pending
                  </span>
                ) : ret.is_valid ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 20, padding: '4px 10px', color: '#86efac', fontSize: 11 }}>
                    <CheckCircle size={11} /> Approved
                  </span>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 20, padding: '4px 10px', color: '#fca5a5', fontSize: 11 }}>
                    <XCircle size={11} /> Rejected
                  </span>
                )}
              </div>

              {/* Details */}
              <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 7, padding: '12px 14px', marginBottom: 14, border: '1px solid rgba(186,130,69,0.06)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {[
                    { label: 'Reason', value: ret.reason },
                    { label: 'Customer', value: ret.orders?.email || ret.customer_email || '—' },
                    { label: 'Order Value', value: ret.orders?.total_amount ? `₹${parseFloat(ret.orders.total_amount).toLocaleString('en-IN')}` : '—' },
                    { label: 'Phone', value: ret.orders?.phone || '—' },
                    { label: 'Condition', value: ret.product_condition || '—' },
                    { label: 'Has Proof', value: ret.has_proof ? '✓ Yes' : ret.has_proof === false ? '✗ No' : '—' },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <div style={{ fontSize: 9, letterSpacing: '0.3em', color: 'rgba(245,240,232,0.2)', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
                      <div style={{ fontSize: 13, color: 'rgba(245,240,232,0.7)' }}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Return Tracking ID */}
              {ret.is_valid === true && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 9, letterSpacing: '0.3em', color: 'rgba(245,240,232,0.2)', textTransform: 'uppercase', marginBottom: 8 }}>
                    Return Tracking ID {ret.return_tracking_id ? '✓ Assigned' : ''}
                  </div>
                  {ret.return_tracking_id ? (
                    <div style={{ fontSize: 13, color: '#ba8245', background: 'rgba(186,130,69,0.08)', borderRadius: 6, padding: '8px 12px', border: '1px solid rgba(186,130,69,0.2)' }}>
                      {ret.return_tracking_id}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        value={trackingInputs[ret.id] || ''}
                        onChange={e => setTrackingInputs(prev => ({ ...prev, [ret.id]: e.target.value }))}
                        placeholder="Enter return tracking ID..."
                        style={{
                          flex: 1, background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(186,130,69,0.15)', borderRadius: 6,
                          padding: '8px 12px', color: '#f5f0e8', fontSize: 13,
                          outline: 'none', fontFamily: "'Cormorant Garamond', Georgia, serif",
                        }}
                      />
                      <button
                        onClick={() => assignReturnTracking(ret.id)}
                        disabled={!trackingInputs[ret.id]?.trim() || sendingTracking[ret.id]}
                        style={{
                          background: '#ba8245', border: 'none', borderRadius: 6,
                          padding: '8px 12px', color: '#0f0d0a', cursor: 'pointer',
                          fontFamily: "'Cormorant Garamond', Georgia, serif",
                          opacity: (!trackingInputs[ret.id]?.trim() || sendingTracking[ret.id]) ? 0.5 : 1,
                          display: 'flex', alignItems: 'center', gap: 5, fontSize: 12,
                        }}
                      >
                        <Send size={12} /> {sendingTracking[ret.id] ? '...' : 'Assign & Email'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Date */}
              {ret.created_at && (
                <div style={{ fontSize: 11, color: 'rgba(245,240,232,0.25)', marginBottom: 14 }}>
                  Submitted: {new Date(ret.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
              )}

              {/* Action Buttons */}
              {ret.is_valid === null && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => updateReturn(ret.id, { is_valid: true })}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)',
                      borderRadius: 6, padding: '9px', color: '#86efac',
                      fontSize: 12, cursor: 'pointer', letterSpacing: '0.05em',
                      fontFamily: "'Cormorant Garamond', Georgia, serif",
                    }}
                  >
                    <CheckCircle size={13} /> Approve
                  </button>
                  <button
                    onClick={() => updateReturn(ret.id, { is_valid: false })}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
                      borderRadius: 6, padding: '9px', color: '#fca5a5',
                      fontSize: 12, cursor: 'pointer', letterSpacing: '0.05em',
                      fontFamily: "'Cormorant Garamond', Georgia, serif",
                    }}
                  >
                    <XCircle size={13} /> Reject
                  </button>
                </div>
              )}
              {ret.is_valid !== null && (
                <button
                  onClick={() => updateReturn(ret.id, { is_valid: null })}
                  style={{
                    width: '100%', background: 'transparent',
                    border: '1px solid rgba(186,130,69,0.1)',
                    borderRadius: 6, padding: '8px', color: 'rgba(245,240,232,0.3)',
                    fontSize: 11, cursor: 'pointer', letterSpacing: '0.1em',
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                  }}
                >
                  Reset to Pending
                </button>
              )}
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 48, color: 'rgba(245,240,232,0.2)' }}>
              No returns in this category
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminReturns;